import { DateTime } from 'luxon';

/**
 * Formats a wall-clock time string ("HH:mm" or "HH:mm:ss") as 12-hour display,
 * e.g. "09:00" or "09:00:00" → "9:00 AM". Pure string math — no DateTime, no
 * timezone, no conversion. Safe to use for pickup window times that are
 * location-relative and must never be shifted.
 */
export const formatWallClockTime = (time: string | null | undefined): string => {
  if (!time) return '';
  const [hourStr, minuteStr] = time.split(':');
  const hour = parseInt(hourStr, 10);
  if (isNaN(hour)) return time;
  const minute = minuteStr ?? '00';
  const period = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 === 0 ? 12 : hour % 12;
  return `${displayHour}:${minute} ${period}`;
};

/**
 * Formats the date portion of an ISO string as a full locale date, e.g.
 * "Monday, June 1, 2026". Strips the time component and parses in UTC so
 * toLocaleString formats in UTC — a date-only value at midnight UTC is the
 * same date for every viewer regardless of their machine timezone.
 */
export const formatWallClockDate = (isoStr: string | null | undefined): string => {
  if (!isoStr) return '';
  const datePart = isoStr.split('T')[0];
  const dt = DateTime.fromISO(datePart, { zone: 'utc' });
  return dt.isValid ? dt.toLocaleString(DateTime.DATE_HUGE) : isoStr;
};

/**
 * Short timezone abbreviation (e.g. "CST"/"CDT") for an IANA zone, DST-resolved
 * against referenceDateIso (falls back to today). Returns '' when the zone is
 * missing/invalid so callers render no label. This is a DISPLAY LABEL ONLY —
 * it is never used to convert a wall-clock time value.
 */
export const getTimeZoneAbbreviation = (ianaTimeZone: string | null | undefined, referenceDateIso?: string | null): string => {
  if (!ianaTimeZone) return '';
  const base = referenceDateIso ? DateTime.fromISO(referenceDateIso, { zone: ianaTimeZone }) : DateTime.now().setZone(ianaTimeZone);
  return base.isValid ? (base.offsetNameShort ?? '') : '';
};
